import * as React from 'react';
import type { Client } from '../features/dashboard/types/dashboard';

type SelectedClientContextType = {
  client: Client | null;
  insuredId: string | undefined;
  hasSelectedClient: boolean;
  setClient: (client: Client) => void;
  clearClient: () => void;
};

const STORAGE_KEY = 'selectedClient';
const SelectedClientContext = React.createContext<SelectedClientContextType | undefined>(undefined);

function readSession(): Client | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as Client;
  } catch {}
  return null;
}

export function SelectedClientProvider({ children }: { children: React.ReactNode }) {
  const [client, setClientState] = React.useState<Client | null>(() => readSession());

  const insuredId = client?.id;
  const hasSelectedClient = client !== null;

  const setClient = React.useCallback((newClient: Client) => {
    setClientState(newClient);
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newClient)); } catch {}
  }, []);

  const clearClient = React.useCallback(() => {
    setClientState(null);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  const value = React.useMemo(
    () => ({ client, insuredId, hasSelectedClient, setClient, clearClient }),
    [client, insuredId, hasSelectedClient, setClient, clearClient]
  );

  return (
    <SelectedClientContext.Provider value={value}>
      {children}
    </SelectedClientContext.Provider>
  );
}

export function useSelectedClient(): SelectedClientContextType {
  const context = React.useContext(SelectedClientContext);
  if (!context) {
    throw new Error('useSelectedClient must be used within a SelectedClientProvider');
  }
  return context;
}
